/**
 * Hızlı PNG yakalama — html2canvas yok.
 * 1) htmlToPngBlob: inline-style HTML → SVG foreignObject (fişler için en hızlı)
 * 2) elementToPngBlob: DOM alanı klonu → html-to-image (karmaşık modaller)
 *
 * Kalite: varsayılan scale/pixelRatio = 2 (keskin metin, hâlâ hızlı)
 */

import { toBlob as elementToBlob } from 'html-to-image';

const DEFAULT_SCALE = 2;

const embedImagesAsDataUrl = (sourceRoot, cloneRoot) => {
  const sourceImgs = sourceRoot.querySelectorAll('img');
  const cloneImgs = cloneRoot.querySelectorAll('img');
  sourceImgs.forEach((srcImg, index) => {
    const dst = cloneImgs[index];
    if (!dst) return;
    if (!srcImg.complete || !srcImg.naturalWidth) {
      dst.remove();
      return;
    }
    try {
      const c = document.createElement('canvas');
      const w = Math.min(srcImg.naturalWidth, 240);
      const h = Math.round((srcImg.naturalHeight / srcImg.naturalWidth) * w);
      c.width = w;
      c.height = h;
      c.getContext('2d').drawImage(srcImg, 0, 0, w, h);
      dst.src = c.toDataURL('image/png');
    } catch (_) {
      dst.remove();
    }
  });
};

export const htmlToPngBlob = async (html, width = 920, options = {}) => {
  if (!html) return null;
  const scale = options.scale || DEFAULT_SCALE;

  const probe = document.createElement('div');
  probe.style.cssText = `position:fixed;left:-9999px;top:0;width:${width}px;visibility:hidden;`;
  probe.innerHTML = html;
  document.body.appendChild(probe);
  const height = Math.max(probe.scrollHeight, probe.offsetHeight, 400);
  document.body.removeChild(probe);

  const wrapped = html.includes('xmlns="http://www.w3.org/1999/xhtml"')
    ? html
    : `<div xmlns="http://www.w3.org/1999/xhtml">${html}</div>`;

  // viewBox ile CSS boyutu sabit, SVG çıktısı scale katı → daha keskin yazı
  const outW = Math.round(width * scale);
  const outH = Math.round(height * scale);
  const svg = `<?xml version="1.0" encoding="UTF-8"?>` +
    `<svg xmlns="http://www.w3.org/2000/svg" width="${outW}" height="${outH}" viewBox="0 0 ${width} ${height}">` +
    `<foreignObject width="${width}" height="${height}">${wrapped}</foreignObject></svg>`;

  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, outW, outH);
        ctx.drawImage(img, 0, 0, outW, outH);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('PNG oluşturulamadı'))),
          'image/png'
        );
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Görüntü render edilemedi'));
    img.src = url;
  });
};

export const elementToPngBlob = async (element, options = {}) => {
  if (!element) return null;

  const pixelRatio = options.pixelRatio || DEFAULT_SCALE;
  const width = options.width || Math.max(element.scrollWidth, element.offsetWidth, element.clientWidth, 640);
  const clone = element.cloneNode(true);

  clone.querySelectorAll('.animate-spin').forEach((node) => node.remove());
  embedImagesAsDataUrl(element, clone);

  clone.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    `width:${width}px`,
    'max-width:none',
    'max-height:none',
    'height:auto',
    'overflow:visible',
    'background:#ffffff',
    'box-shadow:none',
    'filter:none',
    'opacity:0.01',
    'pointer-events:none',
    'z-index:-1'
  ].join(';');

  document.body.appendChild(clone);
  try {
    await new Promise((r) => requestAnimationFrame(r));
    const blob = await elementToBlob(clone, {
      cacheBust: false,
      pixelRatio,
      backgroundColor: '#ffffff',
      width,
      skipFonts: false,
      style: {
        transform: 'none',
        boxShadow: 'none',
        overflow: 'visible',
        opacity: '1'
      },
      filter: (node) => {
        if (!node || !node.tagName) return true;
        const tag = node.tagName.toUpperCase();
        return tag !== 'SCRIPT' && tag !== 'LINK';
      }
    });
    return blob || null;
  } finally {
    if (document.body.contains(clone)) document.body.removeChild(clone);
  }
};

export const writePngToClipboard = async (blob) => {
  if (!navigator.clipboard || !window.ClipboardItem) {
    throw new Error('Bu tarayıcı panoya görüntü kopyalamayı desteklemiyor');
  }
  try {
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
  } catch (_) {
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': Promise.resolve(blob) })]);
  }
};
